// Canon-structure HTML generator for portal-signed resolutions.
// Structure per Resolution Library phase two.

export interface AdoptionInputs {
  mode: 'meeting' | 'written';
  votes_for?: number | string;
  votes_against?: number | string;
  meeting_date?: string;
}

export interface SigningOfficer {
  name: string;
  office: string;
}

const ON_FILE = '[on file]';

function fmtDate(d?: string) {
  if (!d) return new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export function buildAdoptionStatement(a: AdoptionInputs): string {
  if (a.mode === 'meeting') {
    const f = a.votes_for ?? '';
    const ag = a.votes_against ?? '';
    return `${f} to ${ag} at the Officer Meeting of ${fmtDate(a.meeting_date)}`;
  }
  return 'by written adoption of the undersigned';
}

export interface CanonRenderInputs {
  resolution_number?: string | number;
  resolution_title: string;
  whereas_clauses: string[];
  resolved_clauses: string[];
  adoption: AdoptionInputs;
  adoption_date?: string;
  signing_officer: SigningOfficer;
  signer_id: string; // data-signer-id target for signature embedding
  una: {
    name?: string;
    legal_name?: string;
    ca_filing_number?: string;
    ein?: string;
    address?: string;
  };
  extra_signature_blocks_html?: string;
}

export function renderCanonResolutionHtml(i: CanonRenderInputs): string {
  const legalName = i.una.legal_name || i.una.name || ON_FILE;
  const filing = i.una.ca_filing_number || ON_FILE;
  const ein = i.una.ein || ON_FILE;
  const addr = i.una.address || ON_FILE;
  const number = i.resolution_number ? String(i.resolution_number) : '';
  const titleUpper = (i.resolution_title || 'Untitled').toUpperCase();
  const titleLine = number
    ? `RESOLUTION ${number}: ${titleUpper}`
    : `RESOLUTION: ${titleUpper}`;

  const whereasHtml = (i.whereas_clauses || []).map((c, idx, arr) => {
    const isLast = idx === arr.length - 1;
    const end = isLast ? '; and' : ';';
    return `<p style="margin-left:20px;margin-bottom:12px;text-align:justify;"><strong>WHEREAS,</strong> ${c}${end}</p>`;
  }).join('\n');

  const resolvedHtml = (i.resolved_clauses || []).map((c, idx, arr) => {
    const isLast = idx === arr.length - 1;
    const end = isLast ? '.' : '; and be it further';
    return `<p style="margin-left:20px;margin-bottom:12px;text-align:justify;"><strong>RESOLVED,</strong> ${c}${end}</p>`;
  }).join('\n');

  const adoptionStatement = buildAdoptionStatement(i.adoption);
  const adoptionDate = fmtDate(i.adoption_date);
  const officerName = i.signing_officer.name || ON_FILE;
  const officerOffice = i.signing_officer.office || ON_FILE;

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>${i.resolution_title}</title>
  <style>
    @page { size: letter; margin: 1in; @bottom-center { content: "Page " counter(page) " of " counter(pages); font-family: Cambria, Georgia, serif; font-size: 10pt; padding-bottom: 0.25in; } }
    body { font-family: Cambria, Georgia, serif; line-height: 1.6; color: #222; margin: 0; padding: 0; }
    .title-line { text-align: center; font-weight: bold; font-size: 14pt; letter-spacing: 0.5px; margin-bottom: 18px; }
    .legal-header { text-align: center; font-size: 11pt; margin-bottom: 24px; padding-bottom: 14px; border-bottom: 1px solid #666; }
    .legal-header .name { font-weight: bold; font-size: 12pt; }
    .legal-header .row { margin-top: 2px; }
    .section-divider { text-align: center; font-weight: bold; font-size: 11pt; margin: 22px 0 16px; text-transform: uppercase; letter-spacing: 0.5px; }
    .adoption-line { margin-top: 26px; text-align: justify; }
    .signature-block { margin-top: 34px; padding: 15px; border: 1px solid #666; page-break-inside: avoid; }
    .signature-line { border-bottom: 2px solid #333; min-height: 0.6in; width: 3.5in; margin-bottom: 0.15in; padding: 0.1in; }
    .signature-label { font-size: 11pt; margin-top: 0.1in; }
    p { margin-bottom: 12px; text-align: justify; }
  </style>
</head>
<body>
  <div class="title-line">${titleLine}</div>
  <div class="legal-header">
    <div class="name">${legalName}</div>
    <div class="row">a California unincorporated nonprofit association</div>
    <div class="row">CA Filing No.: ${filing}</div>
    <div class="row">EIN: ${ein}</div>
    <div class="row">Principal address: ${addr}</div>
  </div>

  <div>
    ${whereasHtml}
    <div class="section-divider">NOW, THEREFORE, BE IT RESOLVED:</div>
    ${resolvedHtml}
  </div>

  <p class="adoption-line">Adopted ${adoptionStatement} this ${adoptionDate}.</p>

  <div class="signature-block">
    <div class="signature-line" data-signer-id="${i.signer_id}" data-signature-role="primary_officer"></div>
    <div class="signature-label">By: ${officerName}, ${officerOffice}, signing solely in representative capacity for the Association, not individually.</div>
    <div style="margin-top: 0.15in;">Date: ${adoptionDate}</div>
  </div>

  ${i.extra_signature_blocks_html || ''}
</body>
</html>`;
}

// Given a document_templates.template_content string with {{token}} placeholders
// and raw HTML comments, produce a merged HTML. If no signature target is
// present, append the canon signature block so signing works uniformly.
export function renderStandardFormHtml(
  templateContent: string,
  fieldValues: Record<string, string | number | undefined>,
  canonInputs: CanonRenderInputs,
): string {
  let out = templateContent || '';
  // Merge all field values.
  const values: Record<string, string> = {};
  Object.entries(fieldValues || {}).forEach(([k, v]) => {
    values[k] = v == null || v === '' ? '' : String(v);
  });
  // Also expose canonical system values.
  values.una_name = canonInputs.una.legal_name || canonInputs.una.name || ON_FILE;
  values.legal_name = values.una_name;
  values.ca_filing_number = canonInputs.una.ca_filing_number || ON_FILE;
  values.una_ein = canonInputs.una.ein || ON_FILE;
  values.una_address = canonInputs.una.address || ON_FILE;
  values.resolution_number = canonInputs.resolution_number ? String(canonInputs.resolution_number) : '';
  values.signature_date = canonInputs.adoption_date || new Date().toISOString().split('T')[0];
  values.signing_officer_name = canonInputs.signing_officer.name;
  values.signing_officer_office = canonInputs.signing_officer.office;

  out = out.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_m, key) => values[key] ?? '');
  out = out.replace(/<!--[\s\S]*?-->/g, '');

  if (!/data-signer-id=/.test(out)) {
    // Ensure the signature block exists so the finalize path works.
    const sigBlock = `
    <div class="signature-block" style="margin-top:34px;padding:15px;border:1px solid #666;page-break-inside:avoid;">
      <div class="signature-line" data-signer-id="${canonInputs.signer_id}" data-signature-role="primary_officer" style="border-bottom:2px solid #333;min-height:0.6in;width:3.5in;margin-bottom:0.15in;padding:0.1in;"></div>
      <div style="font-size:11pt;margin-top:0.1in;">By: ${canonInputs.signing_officer.name}, ${canonInputs.signing_officer.office}, signing solely in representative capacity for the Association, not individually.</div>
      <div style="margin-top:0.15in;">Date: ${canonInputs.adoption_date ? new Date(canonInputs.adoption_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}</div>
    </div>`;
    // Append before </body> if present, else at end.
    if (/<\/body>/i.test(out)) {
      out = out.replace(/<\/body>/i, `${sigBlock}\n</body>`);
    } else {
      out = out + sigBlock;
    }
  }
  return out;
}
